import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { color } from '../../../app/theme';
import { sortType } from '../../../app/types';
import { TWD, downloadImage } from '../../../app/utils';
import IconDelete from '../../../assets/images/Icons/IconDelete';
import IconDownload20 from '../../../assets/images/Icons/IconDownload20';
import IconRefresh from '../../../assets/images/Icons/IconRefresh';
import IconTableSort from '../../../assets/images/Icons/IconTableSort';
import IconTableSortDown from '../../../assets/images/Icons/IconTableSortDown';
import IconTableSortUp from '../../../assets/images/Icons/IconTableSortUp';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import Checkbox from '../../shared/Checkbox';
import Table from '../../shared/Table';
import Tooltip from '../../shared/Tooltip';
import MemberPerformanceEmpty from './Empty';

const useStyles = makeStyles(
  () => ({
    headerCell: {
      height: 58,
      backgroundColor: color.$7,
    },
    column: {
      display: 'flex',
      alignItems: 'center',
    },
    cell: {
      fontSize: 15,
      color: color.$14,
      border: 'none',
      height: 61,
    },
    cellName: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      '& > *': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    index: {
      textAlign: 'center',
    },
    row: {
      cursor: 'pointer',
      '&:hover > div': {
        backgroundColor: color.$19,
      },
    },
    name: {
      minHeight: 13,
      fontWeight: '400',
      fontSize: 13,
      lineHeight: 1,
      color: color.$13,
      margin: '3px 0',
      maxWidth: '100%',
      width: 'min-content',
      display: 'flex',
      alignItems: 'center',
      '& > div': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    url: {
      fontWeight: '400',
      fontSize: '13px',
      lineHeight: '13px',
      color: `${color.$2}`,
      margin: '3px 0',
      textDecoration: 'underline',
    },
    loading: {
      margin: '350px 0',
    },
    activatedIcon: {
      width: 8,
      height: 8,
      margin: '0 8px 0 0',
      borderRadius: '20px',
    },
    activatedText: {
      fontSize: 15,
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    activatedBlock: {
      display: 'flex',
      alignItems: 'center',
    },
    tooltip: {
      lineHeight: 2,
    },
    downloadUrl: {
      color: color.$2,
      textDecoration: 'underline',
      marginLeft: 8,
    },
  }),
  {
    name: 'DatasetTable',
  },
);

export const num = v => TWD(v || 0, false).format();

const DatasetTable = ({
  dataType,
  list,
  selectedList,
  setSelectedList,
  sort,
  setSort,
  onLazyLoad,
  onDelete,
  onRefresh,
  isLoading = true,
  scrollRef,
  progress,
  isSelectAll,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { pathKey } = useSelector(store => store.aiBot);

  const [isLazyLoad, setIsLazyLoad] = useState(false);
  const isLazyLoadRef = useRef(null);
  isLazyLoadRef.current = isLazyLoad;

  const handleChangeColumnSort = key => {
    if (progress.done) {
      switch (sort?.[key]) {
        case sortType.ASC:
          setSort({ [key]: sortType.DESC });
          break;
        case sortType.DESC:
          setSort(undefined);
          break;
        default:
          setSort({ [key]: sortType.ASC });
          break;
      }
    }
  };

  const generateSortComponent = key => {
    if (progress.done) {
      if (sort?.[key]) {
        if (sort?.[key] === sortType.ASC) {
          return <IconTableSortUp />;
        }

        return <IconTableSortDown />;
      }

      return <IconTableSort />;
    }
    return null;
  };

  const getActivated = status => {
    let textColor;
    let text;

    switch (status) {
      case 'done':
        textColor = '#1ea079';
        text = t(`${pathKey}:statusTypes.done`);
        break;
      case 'embedding':
        textColor = '#dea912';
        text = t(`${pathKey}:statusTypes.embedding`);
        break;
      case 'error':
      default:
        textColor = '#e95445';
        text = t(`${pathKey}:statusTypes.error`);
        break;
    }

    return (
      <div className={classes.activatedBlock}>
        <div
          className={classes.activatedIcon}
          style={{
            backgroundColor: textColor,
          }}
        />
        <div
          className={classes.activatedText}
          style={{
            color: textColor,
          }}
        >
          {text}
        </div>
      </div>
    );
  };

  const columns = useMemo(() => {
    return [
      {
        content: <div className={classes.column}>{t(`${pathKey}:index`)}</div>,
        key: 'index',
        className: classes.headerCell,
        style: { width: 50 },
      },
      {
        content: <div className={classes.column}>{t(`${pathKey}:name`)}</div>,
        key: 'name',
        className: classes.headerCell,
        style: { maxWidth: 379, width: 379 },
      },
      {
        content: <div className={classes.column}>{t(`${pathKey}:status`)}</div>,
        key: 'status',
        className: classes.headerCell,
        style: { width: 120, minWidth: 120 },
      },
      {
        content: (
          <div className={classes.column}>
            {generateSortComponent('wordCount')}
            {t(`${pathKey}:wordCount`)}
          </div>
        ),
        key: 'wordCount',
        className: classes.headerCell,
        style: { width: 100, minWidth: 100, cursor: 'pointer' },
        onClick: () => handleChangeColumnSort('wordCount'),
      },
      {
        content: (
          <div className={classes.column}>
            {generateSortComponent('updatedAt')}
            {dataType === 'web'
              ? t(`${pathKey}:updatedAt`)
              : t(`${pathKey}:createdAt`)}
          </div>
        ),
        key: 'updatedAt',
        className: classes.headerCell,
        style: { minWidth: 200, width: 'auto', cursor: 'pointer' },
        onClick: () => handleChangeColumnSort('updatedAt'),
      },
      ...(dataType === 'web'
        ? [
            {
              content: (
                <div className={classes.column}>
                  {t(`${pathKey}:onUpdate`)}
                  <Tooltip
                    light
                    placement="top"
                    title={t(`${pathKey}:onUpdateTooltip`)}
                  >
                    <img
                      src={icHelpCircle}
                      style={{ marginLeft: 4 }}
                      alt="alert"
                    />
                  </Tooltip>
                </div>
              ),
              key: 'onUpdate',
              className: classes.headerCell,
              style: { width: 80, minWidth: 80 },
            },
          ]
        : []),
      {
        content: (
          <div className={classes.column}>{t(`${pathKey}:onDelete`)}</div>
        ),
        key: 'onDelete',
        className: classes.headerCell,
        style: { width: 80, minWidth: 80 },
      },
    ];
  }, [sort, dataType, progress.done]);

  const data = useMemo(() => {
    return list.map((d, i) => ({
      ...d,
      index: (v, isHover) => {
        const isSelected = !!selectedList.find(s => s === d?.id);

        if (isHover || isSelected) {
          return (
            <Checkbox
              checked={isSelectAll || isSelected}
              onClick={() => {
                if (selectedList?.find(s => s === v?.id)) {
                  setSelectedList(selectedList.filter(s => s !== v?.id));
                } else {
                  setSelectedList([...selectedList, v?.id]);
                }
              }}
            />
          );
        }

        return <div className={classes.index}>{i + 1}</div>;
      },
      name: (v, isHover) => {
        return (
          <div
            className={classnames(classes.cell, classes.cellName)}
            style={{ width: 363 }}
          >
            <div className={classes.name}>
              {d?.name && (
                <Tooltip
                  isComponent
                  placement="top"
                  title={
                    <div style={{ lineHeight: 1.4 }}>
                      {d?.name}
                      {d?.name && <br />}
                      {dataType === 'web' && d?.url}
                    </div>
                  }
                >
                  <div>{d?.name}</div>
                </Tooltip>
              )}
              {dataType === 'file' && d?.url && isHover && (
                <Tooltip
                  isComponent
                  placement="top"
                  title={t(`${pathKey}:download`)}
                >
                  <span
                    className={classes.downloadUrl}
                    onClick={e => {
                      e.stopPropagation();
                      downloadImage(d?.url);
                    }}
                  >
                    <IconDownload20
                      className={classes.icon}
                      id={`icon-delete-all-${i}`}
                    />
                  </span>
                </Tooltip>
              )}
            </div>

            {dataType === 'web' && (
              <a
                className={classes.url}
                href={d?.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {d?.url}
              </a>
            )}
          </div>
        );
      },
      status: getActivated(d?.status),
      wordCount: num(d?.wordCount),
      updatedAt: moment(d?.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
      onUpdate: (
        <Tooltip placement="top" title={t(`${pathKey}:onUpdate`)}>
          <span>
            <IconRefresh
              className={classes.icon}
              id={`icon-refresh-all-${i}`}
              width={20}
              height={20}
              color={!progress.done ? color.$9 : color.$12}
              hoverColor={!progress.done ? color.$9 : color.$10}
              onClick={() => {
                if (progress.done) {
                  onRefresh(d);
                }
              }}
            />
          </span>
        </Tooltip>
      ),
      onDelete: (
        <Tooltip placement="top" title={t(`${pathKey}:onDelete`)}>
          <span>
            <IconDelete
              className={classes.icon}
              id={`icon-delete-all-${i}`}
              width={20}
              height={20}
              color={!progress.done ? color.$9 : color.$12}
              hoverColor={!progress.done ? color.$9 : color.$10}
              onClick={() => {
                if (progress.done) {
                  onDelete(d);
                }
              }}
            />
          </span>
        </Tooltip>
      ),
    }));
  }, [list, selectedList, progress.done]);

  const handleScroll = async () => {
    const $table = scrollRef.current;
    const { scrollHeight, scrollTop, clientHeight } = $table;

    if (
      !isLazyLoadRef.current &&
      scrollHeight - (scrollTop + clientHeight) < 50
    ) {
      setIsLazyLoad(true);
      await onLazyLoad();
      setIsLazyLoad(false);
    }
  };

  useEffect(() => {
    const $table = scrollRef.current;
    $table.removeEventListener('scroll', handleScroll);
    $table.addEventListener('scroll', handleScroll);

    return () => {
      $table.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef]);

  return (
    <Table
      classNames={{
        row: classes.row,
        loading: classes.loading,
      }}
      stickyHeaderCount={0}
      leftStickyColumnCount={2}
      columns={columns}
      data={data}
      isLoading={isLoading}
      emptyComponent={<MemberPerformanceEmpty />}
    />
  );
};

export default DatasetTable;
